import { setPluginAccessToken } from '~/services/bridge/pluginAuth'
import { UpdateProfileReq, User } from '~/types/ResponseType'
import { resolveApiUrl } from '~/utils/apiUrl'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiErrorMessage, parseResponseBody } from '~/utils/shared'
import { getDeviceId } from '~/utils/storage'

export const getBlockedEmailDomains = async (): Promise<string[]> => {
  const response = await fetch(resolveApiUrl('/api/user/blocked-domains'), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(
      await parseResponseBody(response),
      'Failed to fetch blocked domains'
    )
    throw new Error(`Failed to get blocked domains. ${reason}`)
  }
  const data = await response.json()
  return data.domains || []
}

export const checkEmailBlocked = async (
  email: string
): Promise<{ blocked: boolean; reason?: string }> => {
  const response = await fetch(resolveApiUrl('/api/user/check-email-blocked'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  })
  if (!response.ok) {
    // On failure, assume not blocked so user can proceed to server-side validation
    return { blocked: false }
  }
  return response.json()
}

export const signup = async (
  email: string,
  password: string,
  code: string,
  fromCampaign: boolean = false,
  plan: string = 'flex'
) => {
  const response = await fetch(resolveApiUrl('/api/user/signup'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email,
      password,
      code,
      from_campaign: fromCampaign,
      plan
    })
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to Sign Up. ${reason}`)
  }
  const data = await response.json()
  return data
}

export const joinWaitlist = async (email: string) => {
  const response = await fetch(resolveApiUrl('/api/campaign/join-waitlist'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      uid: getDeviceId(),
      email
    })
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(
      await parseResponseBody(response),
      'Failed to join waitlist'
    )
    throw new Error(reason)
  }
}

export const sendVerificationCode = async (email: string) => {
  const response = await fetch(resolveApiUrl('/api/v1/user/email/send-verification'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  })
  if (!response.ok) {
    const responseJson = await parseResponseBody(response)
    const detail = (responseJson as { detail?: unknown }).detail
    const reason = Array.isArray(detail)
      ? detail[0]?.msg || 'Unknown error'
      : extractApiErrorMessage(responseJson, 'Unknown error')
    throw new Error(`Failed to send verification code. ${reason}`)
  }
  return (await response.json()) as { referer: string }
}

export const sendResetPasswordVerification = async (email: string) => {
  const response = await fetch(resolveApiUrl('/api/v1/user/email/reset-password'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(
      await parseResponseBody(response),
      'Failed to send reset password verification'
    )
    throw new Error(`Failed to send reset password verification. ${reason}`)
  }
}

async function readSigninJson<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (!text.trim()) {
    throw new Error(
      `Sign in returned an empty response body (HTTP ${response.status}). Make sure the local API server is running and handling /api/user/signin.`
    )
  }

  try {
    return JSON.parse(text) as T
  } catch (error) {
    if (text.trimStart().startsWith('<')) {
      throw new Error(
        'Sign in reached the frontend server instead of the API server. Make sure localhost:3000 is running the platform API, not only the frontend dev server.'
      )
    }
    throw new Error(
      `Sign in returned invalid JSON: ${error instanceof Error ? error.message : 'Parse error'}`
    )
  }
}

export const signin = async (email: string, password: string, mfa_code: string | null) => {
  const response = await fetch(resolveApiUrl('/api/user/signin'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ email, password, mfa_code })
  })

  const data = await readSigninJson<Record<string, unknown>>(response)
  if (!response.ok) {
    const reason = extractApiErrorMessage(data, 'Unknown error')
    throw new Error(`Failed to Sign In. ${reason}`)
  }
  if ('email_verified' in data && !data.email_verified) {
    return { ...data, email_verified: false }
  } else if ('onboarded' in data && !data.onboarded) {
    return { ...data, onboarded: false }
  } else if (data.mfa_enabled) {
    return { ...data, mfa_required: true }
  }

  const token =
    typeof data.access_token === 'string'
      ? data.access_token
      : typeof data.token === 'string'
        ? data.token
        : undefined
  if (token) {
    localStorage.setItem('token', token)
    setPluginAccessToken(token)
  }

  return data as { referer: string }
}

export const ssoSignin = async () => {
  const response = await fetch(resolveApiUrl('/sso/microsoft/login'), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to Sign In. ${reason}`)
  }
}

export const getUserProfile = async (): Promise<User> => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper(`/api/user/profile`, {
    headers,
    suppressErrorToast: true
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to get user profile. ${reason}`)
  }
  return (await response.json()) as User
}

export const updateUserProfile = async (user: Partial<UpdateProfileReq>) => {
  const response = await fetchWrapper('/api/user/profile', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(user)
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to update profile. ${reason}`)
  }
  return (await response.json()) as User
}

export const deleteAccount = async () => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper(`/api/user/delete-account`, { headers })

  const json = await parseResponseBody(response)
  if (response.status !== 200) {
    throw new Error(extractApiErrorMessage(json))
  }
  return (json as { detail?: unknown }).detail
}

export const emailverify = async (email: string, code: string) => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper(`/api/user/email/verify`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ email, code })
  })

  const json = await parseResponseBody(response)
  if (response.status !== 200) {
    throw new Error(extractApiErrorMessage(json))
  }
  return (json as { detail?: unknown }).detail
}

export const resetPassword = async (
  email: string,
  verificationCode: string,
  newPassword: string
) => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetch(resolveApiUrl('/api/v1/user/email/verify-reset-password'), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      email,
      code: verificationCode,
      password: newPassword
    })
  })

  const message = await parseResponseBody(response)
  if (response.status !== 200) {
    throw new Error(extractApiErrorMessage(message))
  }
  return (message as { detail?: unknown }).detail
}

export const emailCodeVerification = async (email: string, code: string) => {
  const response = await fetch(resolveApiUrl('/api/v1/user/email/verify-email-code'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, code })
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to verify email. ${reason}`)
  }
  const data = await response.json()
  return data
}

export const signupUnverified = async (email: string, password: string, plan: string = 'flex') => {
  const response = await fetch(resolveApiUrl('/api/user/signup'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, password, plan })
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to Sign Up unverified email. ${reason}`)
  }
  const data = await response.json()
  return data
}

export const signupVerifyEmail = async (email: string, code: string, plan: string = 'flex') => {
  const response = await fetch(resolveApiUrl('/api/user/email/verify'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email, password: null, code, plan })
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to verify email. ${reason}`)
  }
  const data = await response.json()
  return data
}

export const completeOnboarding = async () => {
  const response = await fetchWrapper('/api/user/complete-onboarding', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  })
  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to complete onboarding. ${reason}`)
  }
  const data = await response.json()
  return data
}

export interface Session {
  device: string
  last_active: string
  is_current_device: boolean
}

interface SessionsResponse {
  sessions: Session[]
}

export const getSessions = async (): Promise<SessionsResponse> => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper('/api/user/sessions', { headers })

  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to get sessions. ${reason}`)
  }

  const data = await response.json()
  return data
}

export const deleteSession = async (deviceName: string): Promise<void> => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper('/api/user/sessions', {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ device_name: deviceName })
  })

  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to delete session. ${reason}`)
  }
}

export const getAccessLogs = async () => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const response = await fetchWrapper('/api/user/access-logs', { headers })

  if (!response.ok) {
    const reason = extractApiErrorMessage(await parseResponseBody(response), 'Unknown error')
    throw new Error(`Failed to get access logs. ${reason}`)
  }

  const data = await response.json()
  return data
}

// Phone OTP Services for signup flow
export const sendPhoneOTP = async (phone: string, email: string) => {
  const body = { phone, email }

  const response = await fetch(resolveApiUrl('/api/user/send-otp'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const responseJson = await response.json()
    let reason = 'Unknown error'

    if (responseJson.detail) {
      if (typeof responseJson.detail === 'string') {
        reason = responseJson.detail
      } else if (responseJson.detail.error?.details) {
        reason = responseJson.detail.error.details
      } else if (responseJson.detail.message) {
        reason = responseJson.detail.message
      }
    } else if (responseJson.message) {
      reason = responseJson.message
    }

    throw new Error(`Failed to send OTP. ${reason}`)
  }

  const data = await response.json()
  return data
}

export const verifyPhoneOTP = async (phone: string, code: string, email: string) => {
  const body = { phone, code, email }

  const response = await fetch(resolveApiUrl('/api/user/verify-otp'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const responseJson = await response.json()
    let reason = 'Unknown error'

    if (responseJson.detail) {
      if (typeof responseJson.detail === 'string') {
        reason = responseJson.detail
      } else if (responseJson.detail.error) {
        // Handle backend OTP service error format: {"success": false, "error": "INVALID_OTP"}
        if (typeof responseJson.detail.error === 'string') {
          const errorCode = responseJson.detail.error
          // Convert technical error codes to user-friendly messages
          const errorMessages = {
            INVALID_OTP: 'The verification code you entered is incorrect. Please try again.',
            OTP_EXPIRED: 'Your verification code has expired. Please request a new one.',
            NO_OTP_FOUND: 'No verification code found. Please request a new one.',
            MAX_ATTEMPTS_EXCEEDED:
              'Too many incorrect attempts. Please request a new verification code.',
            USER_NOT_FOUND: 'User not found. Please try signing up again.'
          }
          reason = errorMessages[errorCode as keyof typeof errorMessages] || errorCode
        } else if (responseJson.detail.error.details) {
          reason = responseJson.detail.error.details
        }
      } else if (responseJson.detail.message) {
        reason = responseJson.detail.message
      }
    } else if (responseJson.message) {
      reason = responseJson.message
    }

    throw new Error(reason)
  }

  const data = await response.json()
  return data
}

export const resendPhoneOTP = async (phone: string, email: string) => {
  const body = { phone, email }

  const response = await fetch(resolveApiUrl('/api/user/resend-otp'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const responseJson = await response.json()
    let reason = 'Unknown error'

    if (responseJson.detail) {
      if (typeof responseJson.detail === 'string') {
        reason = responseJson.detail
      } else if (responseJson.detail.error?.details) {
        reason = responseJson.detail.error.details
      } else if (responseJson.detail.message) {
        reason = responseJson.detail.message
      }
    } else if (responseJson.message) {
      reason = responseJson.message
    }

    throw new Error(`Failed to resend OTP. ${reason}`)
  }

  const data = await response.json()
  return data
}
